// src/quality-management/components/ManagerWeeklyCalendarView.tsx
// Refactored with Precision with Warmth design system

import React, { useState, useEffect, useMemo } from 'react';
import { Project, TimeEntry, User, Task } from '../services/api/types';
import { useApi } from '../hooks/useApi';
import { apiService } from '../services/api';
import EditTaskModal from './EditTaskModal';
import { Avatar, Badge, Spinner, EmptyState, Modal, Select, Input, Button } from '../../shared/components/ui';
import { TaskStatus } from '../services/api/types';
import { getEventColor } from '../../shared/lib/eventColors';
import { isoDay, taskCoversDay, plannedHoursForDay } from '../utils/taskSchedule';

interface ManagerWeeklyCalendarViewProps {
  projects: Project[];
  weekStart: Date;
  onCreateTask?: (employeeId: number, employeeName: string, date: Date) => void;
}

interface EmployeeWorkData {
  employee: User;
  dailyWork: { [date: string]: DayWork };
  // Horas previstas atribuídas a cada dia (tarefas sem apontamento naquele dia)
  dailyPlanned: { [date: string]: number };
  weeklyTotal: number;
  projectBreakdown: { [projectId: number]: number };
  taskBreakdown: { [taskId: number]: number };
  efficiency: number;
}

interface DayWork {
  timeEntries: TimeEntry[];
  totalHours: number;
  tasksSummary: { taskId: number; taskName: string; hours: number; projectId: number }[];
}

const ManagerWeeklyCalendarView: React.FC<ManagerWeeklyCalendarViewProps> = ({
  projects,
  weekStart,
  onCreateTask
}) => {
  const [employeeWorkData, setEmployeeWorkData] = useState<EmployeeWorkData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [draggingTaskId, setDraggingTaskId] = useState<number | null>(null);
  const [dropTargetKey, setDropTargetKey] = useState<string | null>(null);
  const [quickTask, setQuickTask] = useState<Task | null>(null);
  const [quickStatus, setQuickStatus] = useState<TaskStatus>('pending');
  const [quickProgress, setQuickProgress] = useState<number>(0);
  const [quickSaving, setQuickSaving] = useState(false);

  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const date = new Date(weekStart);
    date.setDate(weekStart.getDate() + i);
    return date;
  }), [weekStart]);

  const { data: allTasks, loading: tasksLoading } = useApi(
    async () => {
      if (projects.length === 0) return [];
      const taskPromises = projects.map(project =>
        apiService.tasks.getTasksForProject(project.id)
      );
      const tasksArrays = await Promise.all(taskPromises);
      return tasksArrays.flat();
    },
    [projects]
  );

  useEffect(() => {
    const fetchEmployeeTimeData = async () => {
      if (projects.length === 0) {
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const allAssignedEmployeeIds = new Set<number>();
        projects.forEach(project => {
          if (project.assigned_employee_ids && Array.isArray(project.assigned_employee_ids)) {
            project.assigned_employee_ids.forEach(empId => allAssignedEmployeeIds.add(empId));
          }
        });

        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        const startDateStr = weekStart.toISOString().split('T')[0];
        const endDateStr = weekEnd.toISOString().split('T')[0];

        // Fetch real employee data from API using the employee-names endpoint
        // This returns a map of employee.id -> { name, username, role }
        let usersMap: { [key: number]: any } = {};
        try {
          const employeeNames = await apiService.users.getEmployeeNames();
          // employeeNames is { [employeeId]: { name, username, role } }
          Object.entries(employeeNames).forEach(([empIdStr, info]) => {
            const empId = parseInt(empIdStr);
            usersMap[empId] = {
              id: empId,
              name: info.name,
              username: info.username,
              role: info.role
            };
          });
        } catch (error) {
          // Use employee IDs as names when employee names cannot be fetched
        }

        // Ensure all assigned employee IDs have at least a fallback entry
        Array.from(allAssignedEmployeeIds).forEach(empId => {
          if (!usersMap[empId]) {
            usersMap[empId] = {
              id: empId,
              name: `Employee ${empId}`,
              username: `employee_${empId}`,
              role: 'employee'
            };
          }
        });

        const employeeDataPromises = Array.from(allAssignedEmployeeIds).map(async (employeeId) => {
          try {
            const timeEntries = await apiService.timeEntries.getTimeEntriesByUser(
              employeeId,
              startDateStr,
              endDateStr
            );

            const projectIds = new Set(projects.map(p => p.id));
            const filteredEntries = Array.isArray(timeEntries)
              ? timeEntries.filter(entry => projectIds.has(entry.project_id))
              : [];

            const dailyWork: { [date: string]: DayWork } = {};

            weekDays.forEach(day => {
              const dateStr = day.toISOString().split('T')[0];
              const dayEntries = filteredEntries.filter(entry => entry.date === dateStr);

              const tasksSummary = dayEntries.reduce((acc, entry) => {
                const existingTask = acc.find(t => t.taskId === entry.task_id);
                if (existingTask) {
                  existingTask.hours += entry.hours;
                } else {
                  const task = allTasks?.find(t => t.id === entry.task_id);
                  acc.push({
                    taskId: entry.task_id,
                    taskName: task?.name || `Task ${entry.task_id}`,
                    hours: entry.hours,
                    projectId: entry.project_id
                  });
                }
                return acc;
              }, [] as { taskId: number; taskName: string; hours: number; projectId: number }[]);

              dailyWork[dateStr] = {
                timeEntries: dayEntries,
                totalHours: dayEntries.reduce((sum, entry) => sum + entry.hours, 0),
                tasksSummary
              };
            });

            // Tarefas da pessoa que ocupam qualquer dia da semana exibida
            const employeeScheduledTasks = allTasks?.filter(task =>
              task.assigned_employee_ids && task.assigned_employee_ids.includes(employeeId) &&
              weekDays.some(day => taskCoversDay(task, isoDay(day)))
            ) || [];

            // Horas previstas por dia: o total da tarefa dividido pelos seus
            // dias úteis. Quando há apontamento da tarefa no dia, vale o
            // realizado e o previsto não é somado de novo.
            const dailyPlanned: { [date: string]: number } = {};
            weekDays.forEach(day => {
              const dateStr = isoDay(day);
              const apontadasNoDia = new Set(
                filteredEntries.filter(e => e.date === dateStr).map(e => e.task_id)
              );
              dailyPlanned[dateStr] = employeeScheduledTasks
                .filter(task => !apontadasNoDia.has(task.id))
                .reduce((sum, task) => sum + plannedHoursForDay(task, dateStr), 0);
            });

            // Total da semana é a soma dos dias, então nunca diverge deles
            const weeklyTotal = weekDays.reduce((sum, day) => {
              const dateStr = isoDay(day);
              return sum + (dailyWork[dateStr]?.totalHours || 0) + (dailyPlanned[dateStr] || 0);
            }, 0);

            const employeeEfficiency = employeeScheduledTasks.length > 0
              ? employeeScheduledTasks.reduce((sum, task) => sum + (task.completion_percentage || 0), 0) / employeeScheduledTasks.length
              : 0;

            const userData = usersMap[employeeId];
            const employee: User = {
              id: employeeId,
              name: userData?.name || `Employee ${employeeId}`,
              username: userData?.username || `employee${employeeId}`,
              email: userData?.email || `employee${employeeId}@company.com`,
              role: userData?.role || ('employee' as const),
              is_active: userData?.is_active !== undefined ? userData.is_active : true,
              department_id: userData?.department_id,
              department: userData?.department,
              quality_people_id: userData?.quality_people_id,
              created_at: userData?.created_at,
              updated_at: userData?.updated_at
            };

            const projectBreakdown: { [projectId: number]: number } = {};
            filteredEntries.forEach(entry => {
              projectBreakdown[entry.project_id] = (projectBreakdown[entry.project_id] || 0) + entry.hours;
            });

            const taskBreakdown: { [taskId: number]: number } = {};
            filteredEntries.forEach(entry => {
              taskBreakdown[entry.task_id] = (taskBreakdown[entry.task_id] || 0) + entry.hours;
            });

            return {
              employee,
              dailyWork,
              dailyPlanned,
              weeklyTotal,
              projectBreakdown,
              taskBreakdown,
              efficiency: employeeEfficiency
            };
          } catch (error) {
            const userData = usersMap[employeeId];
            const employee: User = {
              id: employeeId,
              name: userData?.name || `Employee ${employeeId}`,
              username: userData?.username || `employee${employeeId}`,
              email: userData?.email || `employee${employeeId}@company.com`,
              role: userData?.role || ('employee' as const),
              is_active: userData?.is_active !== undefined ? userData.is_active : true,
              department_id: userData?.department_id,
              department: userData?.department,
              quality_people_id: userData?.quality_people_id,
              created_at: userData?.created_at,
              updated_at: userData?.updated_at
            };

            return {
              employee,
              dailyWork: {},
              dailyPlanned: {},
              weeklyTotal: 0,
              projectBreakdown: {},
              taskBreakdown: {},
              efficiency: 0
            };
          }
        });

        const employeeData = await Promise.all(employeeDataPromises);
        employeeData.sort((a, b) => b.weeklyTotal - a.weeklyTotal);
        setEmployeeWorkData(employeeData);
      } catch (error) {
        console.error('Error fetching employee time data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployeeTimeData();
  }, [projects, weekStart, weekDays, allTasks]);

  const getTaskName = (taskId: number): string => {
    const task = allTasks?.find(t => t.id === taskId);
    return task?.name || `Task ${taskId}`;
  };

  const getProjectName = (projectId: number): string => {
    const project = projects.find(p => p.id === projectId);
    return project?.name || `Project ${projectId}`;
  };

  const getProjectColor = (projectId: number): string => getEventColor(projectId);

  const getScheduledTasksForEmployeeAndDay = (employeeId: number, date: Date): Task[] => {
    const dateStr = date.toISOString().split('T')[0];
    if (!allTasks || !Array.isArray(allTasks)) return [];

    return allTasks.filter(task => {
      const isAssignedToEmployee = task.assigned_employee_ids && task.assigned_employee_ids.includes(employeeId);
      if (!isAssignedToEmployee) return false;
      // A tarefa ocupa de task_date (início) até due_date (fim), quando há intervalo.
      const startStr = task.task_date ? task.task_date.split('T')[0] : null;
      if (!startStr) return false;
      const endStr = task.due_date ? task.due_date.split('T')[0] : startStr;
      const lo = startStr <= endStr ? startStr : endStr;
      const hi = startStr <= endStr ? endStr : startStr;
      return dateStr >= lo && dateStr <= hi;
    });
  };

  // Marca visual de continuação: a tarefa começou antes deste dia
  const taskStartsOnDay = (task: Task, date: Date): boolean =>
    (task.task_date ? task.task_date.split('T')[0] : null) === isoDay(date);

  // Total do dia: horas apontadas + previsto do dia (já rateado por dia útil).
  // Usa os mesmos valores que compõem o total da semana, por isso os dois
  // nunca divergem.
  const getTotalHoursForDay = (date: Date): number => {
    const dateStr = isoDay(date);
    return employeeWorkData.reduce(
      (total, empData) =>
        total + (empData.dailyWork[dateStr]?.totalHours || 0) + (empData.dailyPlanned[dateStr] || 0),
      0
    );
  };

  const getGrandTotal = (): number => {
    return employeeWorkData.reduce((total, empData) => total + empData.weeklyTotal, 0);
  };

  // O rateio das horas por dia gera frações; exibe sempre arredondado
  const horas = (valor: number): string => `${Math.round(valor)}h`;

  const handleTaskClick = (task: Task) => {
    setSelectedTask(task);
  };

  const handleCloseEditModal = () => {
    setSelectedTask(null);
  };

  const handleTaskUpdated = () => {
    window.location.reload();
  };

  const fmtDate = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  // Arrastar a tarefa para outro dia: estende o intervalo (início..fim) entre as datas.
  const handleTaskDrop = async (task: Task, targetDay: Date) => {
    setDraggingTaskId(null);
    setDropTargetKey(null);
    const startStr = task.task_date ? task.task_date.split('T')[0] : fmtDate(targetDay);
    const targetStr = fmtDate(targetDay);
    const lo = targetStr < startStr ? targetStr : startStr;
    const hi = targetStr > startStr ? targetStr : startStr;
    const curEnd = task.due_date ? task.due_date.split('T')[0] : startStr;
    if (lo === startStr && hi === curEnd) return; // sem mudança
    try {
      await apiService.tasks.updateTask(task.id, { task_date: lo, due_date: hi });
      window.location.reload();
    } catch (e) {
      console.error('Erro ao mover tarefa:', e);
    }
  };

  // Menu de contexto (botão direito): edição rápida de status e progresso.
  const openQuickEdit = (task: Task, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setQuickTask(task);
    setQuickStatus(task.status);
    setQuickProgress(task.completion_percentage || 0);
  };

  const handleQuickSave = async () => {
    if (!quickTask) return;
    setQuickSaving(true);
    try {
      await apiService.tasks.updateTask(quickTask.id, {
        status: quickStatus,
        completion_percentage: quickProgress,
      });
      window.location.reload();
    } catch (e) {
      console.error('Erro ao atualizar tarefa:', e);
      setQuickSaving(false);
    }
  };

  const handleTaskDeleted = () => {
    window.location.reload();
  };

  if (loading || tasksLoading) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 rounded-xl border border-border bg-surface-1 py-16 text-foreground-muted shadow-xs">
        <Spinner size="lg" />
        <div className="text-center">
          <h3 className="font-display text-lg font-semibold text-foreground">Carregando calendário...</h3>
          <p className="mt-1 text-sm">Buscando dados da equipe</p>
        </div>
      </div>
    );
  }

  // If no employees, show empty state without header
  if (employeeWorkData.length === 0) {
    return (
      <EmptyState
        title="Nenhum membro encontrado"
        description="Nenhum funcionário atribuído aos seus projetos nesta semana. Atribua membros da equipe aos projetos para ver o trabalho aqui."
      />
    );
  }

  const colTemplate = { gridTemplateColumns: 'minmax(220px, 1.5fr) repeat(7, minmax(150px, 1fr)) minmax(112px, 0.9fr)' };

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold text-foreground">Calendário da Equipe</h2>
          <p className="mt-1 text-[15px] text-foreground-muted">
            {weekStart.toLocaleDateString('pt-BR', { month: 'long', day: 'numeric' })} - {new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000).toLocaleDateString('pt-BR', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <div className="flex min-w-[96px] flex-col rounded-xl border border-border bg-surface-1 px-4 py-2.5 shadow-xs">
            <span className="font-display text-xl font-semibold text-foreground tabular-nums">{projects.length}</span>
            <span className="text-xs text-foreground-muted">{projects.length === 1 ? 'Projeto' : 'Projetos'}</span>
          </div>
          <div className="flex min-w-[96px] flex-col rounded-xl border border-border bg-surface-1 px-4 py-2.5 shadow-xs">
            <span className="font-display text-xl font-semibold text-foreground tabular-nums">{employeeWorkData.length}</span>
            <span className="text-xs text-foreground-muted">{employeeWorkData.length === 1 ? 'Membro' : 'Membros'}</span>
          </div>
          <div className="flex min-w-[96px] flex-col rounded-xl border border-brand/30 bg-brand/10 px-4 py-2.5 shadow-xs">
            <span className="font-display text-xl font-semibold text-brand tabular-nums">{horas(getGrandTotal())}</span>
            <span className="text-xs text-brand/80">Total de Horas</span>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-border bg-surface-1 shadow-sm">
        <div className="grid min-w-[1120px]" style={colTemplate}>
          {/* Header Row */}
          <div className="flex items-center border-b border-border bg-surface-2 px-4 py-3 text-sm font-semibold text-foreground-muted">
            Equipe ({employeeWorkData.length})
          </div>
          {weekDays.map(day => (
            <div
              key={day.toISOString()}
              className="flex flex-col items-center gap-0.5 border-b border-l border-border bg-surface-2 px-2 py-3"
            >
              <div className="text-xs font-medium uppercase tracking-wide text-foreground-subtle">
                {day.toLocaleDateString('pt-BR', { weekday: 'short' })}
              </div>
              <div className="font-display text-lg font-semibold text-foreground tabular-nums">{day.getDate()}</div>
              <div className="text-xs font-medium text-brand tabular-nums">{Math.round(getTotalHoursForDay(day))}h</div>
            </div>
          ))}
          <div className="flex items-center justify-center border-b border-l border-border bg-surface-2 px-3 py-3 text-center text-sm font-semibold text-foreground-muted">
            Total Semanal
          </div>

          {/* Employee Rows */}
          {employeeWorkData.map(empData => (
            <React.Fragment key={empData.employee.id}>
              <div className="flex flex-col gap-2.5 border-b border-border px-4 py-3">
                <div className="flex items-center gap-3">
                  <Avatar name={empData.employee.name} size="sm" />
                  <span className="font-medium text-foreground">{empData.employee.name}</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(empData.projectBreakdown)
                    .filter(([, hours]) => hours > 0)
                    .map(([projectId, hours]) => (
                      <span
                        key={projectId}
                        className="inline-flex items-center gap-1.5 rounded-full bg-surface-3 px-2 py-0.5 text-xs font-medium text-foreground-muted"
                      >
                        <span
                          className="h-2 w-2 shrink-0 rounded-full"
                          style={{ backgroundColor: getProjectColor(Number(projectId)) }}
                        />
                        {getProjectName(Number(projectId))}: {hours}h
                      </span>
                    ))}
                </div>
                <div className="flex items-center gap-2 text-xs text-foreground-muted">
                  <span>Eficiência:</span>
                  <div className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-surface-4">
                    <div
                      className="h-full rounded-full bg-success transition-all"
                      style={{ width: `${Math.min(empData.efficiency || 0, 100)}%` }}
                    />
                  </div>
                  <span className="font-medium text-foreground tabular-nums">
                    {empData.efficiency ? empData.efficiency.toFixed(0) : 0}%
                  </span>
                </div>
              </div>

              {weekDays.map(day => {
                const dateStr = day.toISOString().split('T')[0];
                const dayWork = empData.dailyWork[dateStr];
                const dayEntries = dayWork?.timeEntries || [];
                const scheduledTasks = getScheduledTasksForEmployeeAndDay(empData.employee.id, day);
                const hasTimeEntries = dayWork && dayWork.totalHours > 0;
                const hasScheduledTasks = scheduledTasks.length > 0;

                return (
                  <div
                    key={`${empData.employee.id}-${dateStr}`}
                    className={`min-h-[88px] border-b border-l border-border p-2 transition-colors ${onCreateTask ? 'cursor-pointer hover:bg-surface-2' : ''} ${dropTargetKey === `${empData.employee.id}-${dateStr}` ? 'bg-brand/10 ring-1 ring-inset ring-brand/40' : ''}`}
                    onClick={() => {
                      if (onCreateTask) {
                        onCreateTask(empData.employee.id, empData.employee.name, day);
                      }
                    }}
                    onDragOver={(e) => {
                      if (draggingTaskId != null) {
                        e.preventDefault();
                        setDropTargetKey(`${empData.employee.id}-${dateStr}`);
                      }
                    }}
                    onDragLeave={() => setDropTargetKey(prev => (prev === `${empData.employee.id}-${dateStr}` ? null : prev))}
                    onDrop={(e) => {
                      e.preventDefault();
                      const t = allTasks?.find(tt => tt.id === draggingTaskId);
                      if (t) handleTaskDrop(t, day);
                    }}
                    data-testid="calendar-day"
                  >
                    {hasTimeEntries || hasScheduledTasks ? (
                      <div className="flex flex-col gap-2">
                        {hasTimeEntries && (
                          <div className="flex flex-col gap-1.5">
                            <div className="text-xs font-semibold text-foreground-muted tabular-nums">
                              {dayWork.totalHours}h registradas
                            </div>
                            {dayEntries.map(entry => (
                              <div
                                key={entry.id}
                                className="rounded-lg border-l-[3px] bg-surface-3 px-2 py-1.5 shadow-xs"
                                style={{ borderLeftColor: getProjectColor(entry.project_id) }}
                              >
                                <div className="text-[13px] font-semibold text-foreground tabular-nums">{entry.hours}h</div>
                                <div className="truncate text-[12px] font-medium text-foreground">{getTaskName(entry.task_id)}</div>
                                <div className="truncate text-[11px] text-foreground-subtle">{getProjectName(entry.project_id)}</div>
                              </div>
                            ))}
                          </div>
                        )}

                        {hasScheduledTasks && (
                          <div className="flex flex-col gap-1.5">
                            <div className="text-[11px] font-medium uppercase tracking-wide text-foreground-subtle">
                              {scheduledTasks.length} tarefa{scheduledTasks.length > 1 ? 's' : ''} agendada{scheduledTasks.length > 1 ? 's' : ''}
                            </div>
                            {scheduledTasks.map(task => {
                              const hasTimeLogged = dayEntries.some(entry => entry.task_id === task.id);

                              return (
                                <div
                                  key={`task-${task.id}`}
                                  draggable
                                  onDragStart={(e) => {
                                    e.stopPropagation();
                                    setDraggingTaskId(task.id);
                                    e.dataTransfer.effectAllowed = 'move';
                                  }}
                                  onDragEnd={() => {
                                    setDraggingTaskId(null);
                                    setDropTargetKey(null);
                                  }}
                                  onContextMenu={(e) => openQuickEdit(task, e)}
                                  className={`cursor-grab rounded-lg border-l-[3px] px-2 py-1.5 shadow-xs transition-colors active:cursor-grabbing ${
                                    hasTimeLogged ? 'bg-success/10 hover:bg-success/15' : 'bg-surface-2 hover:bg-surface-3'
                                  } ${draggingTaskId === task.id ? 'opacity-50' : ''}`}
                                  style={{ borderLeftColor: getProjectColor(task.project_id) }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleTaskClick(task);
                                  }}
                                  title="Arraste para estender · clique para editar · botão direito: status e progresso"
                                >
                                  {!taskStartsOnDay(task, day) && (
                                    <div className="mb-0.5 text-[10px] font-medium uppercase tracking-wide text-foreground-subtle">Continuação</div>
                                  )}
                                  <div className="truncate text-[13px] font-medium text-foreground">{task.name}</div>
                                  <div className="flex items-center gap-2 text-[12px] text-foreground-muted tabular-nums">
                                    <span>{task.expected_hours}h</span>
                                    {(task.completion_percentage || 0) > 0 && (
                                      <span className="text-brand">{task.completion_percentage}%</span>
                                    )}
                                  </div>
                                  <div className="truncate text-[11px] text-foreground-subtle">{getProjectName(task.project_id)}</div>
                                  {hasTimeLogged && (
                                    <Badge tone="success" size="sm" className="mt-1">Registrado</Badge>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex h-full items-center justify-center text-[13px] text-foreground-subtle">
                        Sem tarefas
                      </div>
                    )}
                  </div>
                );
              })}

              <div className="flex flex-col items-center justify-center border-b border-l border-border px-3 py-3">
                <div className="font-display text-lg font-semibold text-foreground tabular-nums">{horas(empData.weeklyTotal)}</div>
                <div className="text-xs text-foreground-subtle">semanal</div>
              </div>
            </React.Fragment>
          ))}

          {/* Grand Total Row */}
          <div className="flex items-center border-t border-border bg-surface-2 px-4 py-3 text-sm font-semibold text-foreground">
            Totais Diários
          </div>
          {weekDays.map(day => (
            <div
              key={`total-${day.toISOString()}`}
              className="flex items-center justify-center border-l border-t border-border bg-surface-2 px-2 py-3 font-display font-semibold text-foreground tabular-nums"
            >
              {Math.round(getTotalHoursForDay(day))}h
            </div>
          ))}
          <div className="flex items-center justify-center border-l border-t border-border bg-brand/10 px-3 py-3 font-display font-semibold text-brand tabular-nums">
            {horas(getGrandTotal())}
          </div>
        </div>
      </div>

      {selectedTask && (
        <EditTaskModal
          task={selectedTask}
          onClose={handleCloseEditModal}
          onTaskUpdated={handleTaskUpdated}
          onTaskDeleted={handleTaskDeleted}
        />
      )}

      {/* Edição rápida (menu de contexto): status e progresso */}
      <Modal
        isOpen={!!quickTask}
        onClose={() => setQuickTask(null)}
        title="Atualizar tarefa"
        description={quickTask?.name}
        size="sm"
        data-testid="quick-edit-modal"
        footer={
          <>
            <Button variant="ghost" onClick={() => setQuickTask(null)} disabled={quickSaving}>
              Cancelar
            </Button>
            <Button onClick={handleQuickSave} loading={quickSaving} data-testid="quick-edit-save">
              Salvar
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <Select
            label="Status"
            value={quickStatus}
            onChange={(e) => setQuickStatus(e.target.value as TaskStatus)}
            data-testid="quick-edit-status"
            options={[
              { value: 'pending', label: 'Pendente' },
              { value: 'in_progress', label: 'Em andamento' },
              { value: 'completed', label: 'Concluído' },
              { value: 'cancelled', label: 'Cancelado' },
            ]}
          />
          <div className="flex flex-col gap-2">
            <Input
              label="Progresso (%)"
              type="number"
              min="0"
              max="100"
              value={quickProgress}
              onChange={(e) => setQuickProgress(Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
              data-testid="quick-edit-progress"
            />
            <div className="h-2 overflow-hidden rounded-full bg-surface-3">
              <div className="h-full rounded-full bg-brand transition-all" style={{ width: `${quickProgress}%` }} />
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ManagerWeeklyCalendarView;
